/**
 * Copyright IBM Corp. 2022
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanAPI from "../api/TriFloorPlanAPI.js";

export default class TriLabelsPlugin extends TriFloorPlanPlugin
{
    static type = "Layers";


    pluginDidUpdate()
    {
        const { floorPlanLoaded, enabled, onLayersChange, layerVisibility } = this.props;
        if( !floorPlanLoaded || !enabled )
        {
            this.stop();
            return;
        }
        this.onLayersChange = onLayersChange;
        this.layerVisibility = layerVisibility;
        this.start();
    }

    pluginDidRemove()
    {
        this.stop();
    }


    start()
    {
        const { triFloorPlan, enabled, floorPlanLoaded } = this.props;
        const { svgElement } = triFloorPlan;
        if( !floorPlanLoaded || !enabled || !svgElement )
        {
            this.stop();
            return;
        }

        const children = svgElement.children;
        let isChanged = false;

        if( this.started === true )
        {
          for (let i = 0; this.layerVisibility && i < this.layerVisibility.length; i++ )
            {
                if( !this.layerVisibility[ i ].loaded
                    && this.layerVisibility[ i ].visible
                    && !this.isLayerLoaded( children, this.layerVisibility[ i ].name ) )
                {
                    this.addLayer( svgElement, this.layerVisibility[ i ].name );
                    this.layerVisibility[ i ].loaded = true;
                    isChanged = true;
                }
            }

            for( let i = 0; i < children.length; i++ )
            {
                const el = children[ i ];
                if( el.tagName !== "g" ) continue;
                if( el.children === null || el.children.length === 0 ) continue;

                for (let i = 0; this.layerVisibility && i < this.layerVisibility.length; i++ )
                {
                    if( !this.layerVisibility[ i ].name ) continue;
                    if(    this.layerVisibility[ i ].name === el.id
                        || (    this.layerVisibility[ i ].name.includes( "|") // XREF
                             && this.layerVisibility[ i ].name.endsWith( el.id ) ) )
                    {
                        this.layerVisibility[ i ].loaded = true;
                        el.style.display = this.layerVisibility[ i ].visible === false ? "none" : "";
                        break;
                    }
                }
            }

            if( this.onLayersChange && isChanged ) this.onLayersChange( this.layers );

            return;
        }

        this.triFloorPlan = triFloorPlan;
        this.loadLayers();

        this.started = true;
    }

    isLayerLoaded( children, layerName )
    {
        for( let i = 0; i < children.length; i++ )
        {
            const el = children[ i ];
            if( el.id === layerName ) return true;
        }
        return false;
    }

    stop()
    {
        this.started = false;
        this.triFloorPlan = null;
    }

    async loadLayers()
    {
        const drawingId = this.triFloorPlan.floorPlanId;

        let layers = null;
        try
        {
            layers = await TriFloorPlanAPI.getLayers( drawingId );
        }
        catch( e )
        {
            const { onLoadError } = this.props;
            if( onLoadError ) onLoadError( e );
            return;
        }
        this.layers = [];
        if( layers && this.onLayersChange )
        {
            for( let i = 0; i < layers.length; i++ )
            {
                this.layers.push(
                {
                  loaded: layers[ i ].visible,
                  ...layers[ i ],
                } );
            }
            this.onLayersChange( this.layers );
        }
    }

    async addLayer( svgElement, layerName )
    {
        const drawingId = this.triFloorPlan.floorPlanId;
        try
        {
            const svgHTML = await TriFloorPlanAPI.getLayer( drawingId, layerName );

            const container = document.createElement( "div" );
            const svgToAdd = "<svg>" + svgHTML + "</svg>";
            container.innerHTML = "" + svgToAdd;
            const nodes = Array.prototype.slice.call( container.childNodes[ 0 ].childNodes );
            nodes.forEach( function ( el )
            {
                svgElement.appendChild( el );
            }, this );
        }
        catch( e )
        {
            const { onLoadError } = this.props;
            if( onLoadError ) onLoadError( e );
            return;
        }
    }
}
